import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { api } from '../api';

const fmt = v => '₹' + Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const BAR_COLORS = ['var(--green)', 'var(--blue)', 'var(--amber)', 'var(--light)'];

export default function TopProductsChart({ data, start, end }) {
  const [selected,  setSelected]  = useState(null);
  const [stateData, setStateData] = useState({});
  const [loading,   setLoading]   = useState(false);

  if (!data || data.length === 0) return null;

  const sliced = data.slice(0, 8).map(p => ({
    ...p,
    name: p.product_name.length > 28 ? p.product_name.slice(0, 28) + '…' : p.product_name,
  }));

  const handleBarClick = async (barData) => {
    const productName = barData?.product_name;
    if (!productName) return;

    if (selected === productName) { setSelected(null); return; }

    setSelected(productName);
    if (!stateData[productName]) {
      setLoading(true);
      try {
        const res = await api.productStates(start, end, productName);
        setStateData(prev => ({ ...prev, [productName]: res }));
      } catch {
        setStateData(prev => ({ ...prev, [productName]: [] }));
      } finally {
        setLoading(false);
      }
    }
  };

  // Build display list — insert breakdown panel right after selected product
  const displayItems = [];
  sliced.forEach((p, i) => {
    displayItems.push({ type: 'product', data: p, index: i });
    if (selected === p.product_name) {
      displayItems.push({ type: 'breakdown', productName: p.product_name });
    }
  });

  const states = selected ? (stateData[selected] || []) : [];
  const maxRev = states.length ? Math.max(...states.map(s => s.revenue)) : 1;

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div className="card-title" style={{ margin: 0 }}>Top products by revenue</div>
        <div style={{ fontSize: '11px', color: 'var(--muted)' }}>Click a bar to see state breakdown</div>
      </div>

      {/* ── Render each product row manually ─── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {displayItems.map((item, idx) => {

          // ── Product bar row ─────────────────── //
          if (item.type === 'product') {
            const p        = item.data;
            const isActive = selected === p.product_name;
            const maxBarRev = Math.max(...sliced.map(s => s.revenue));
            const barWidth  = `${(p.revenue / maxBarRev) * 100}%`;

            return (
              <div
                key={p.product_name}
                onClick={() => handleBarClick(p)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '200px 1fr 110px',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '6px 8px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: isActive ? 'var(--surface-hover)' : 'transparent',
                  border: isActive ? '1px solid var(--accent-border)' : '1px solid transparent',
                  transition: 'all .15s',
                }}
              >
                {/* Product name */}
                <div style={{
                  fontSize: '12px', fontWeight: isActive ? 600 : 400,
                  color: isActive ? 'var(--green-dark)' : 'var(--text)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                  <span style={{
                    fontSize: '10px', color: isActive ? 'var(--green)' : 'var(--muted)',
                    transition: 'transform .2s',
                    display: 'inline-block',
                    transform: isActive ? 'rotate(90deg)' : 'rotate(0deg)',
                  }}>▶</span>
                  {p.name}
                </div>

                {/* Bar track */}
                <div style={{ height: '20px', background: 'var(--surface-3)', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
                  <div style={{
                    height: '100%',
                    width: barWidth,
                    background: isActive ? 'var(--green-dark)' : 'var(--green)',
                    borderRadius: '4px',
                    opacity: selected && !isActive ? 0.35 : 1,
                    transition: 'all .3s ease',
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '6px',
                  }}>
                    {(p.revenue / maxBarRev) > 0.25 && (
                      <span style={{ fontSize: '10px', color: '#fff', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {fmt(p.revenue)}
                      </span>
                    )}
                  </div>
                  {(p.revenue / maxBarRev) <= 0.25 && (
                    <span style={{
                      position: 'absolute', left: `calc(${barWidth} + 6px)`, top: '50%',
                      transform: 'translateY(-50%)', fontSize: '10px', color: 'var(--muted)', whiteSpace: 'nowrap',
                    }}>
                      {fmt(p.revenue)}
                    </span>
                  )}
                </div>

                {/* Qty + Orders */}
                <div style={{ fontSize: '11px', color: 'var(--muted)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {p.qty.toLocaleString('en-IN')} qty · {p.orders} orders
                </div>
              </div>
            );
          }

          // ── State breakdown panel (below selected bar) ── //
          if (item.type === 'breakdown') {
            return (
              <div
                key="breakdown-panel"
                style={{
                  margin: '2px 0 6px 210px',
                  background: 'var(--surface-tint-2)',
                  border: '1px solid var(--accent-border)',
                  borderTop: '2px solid var(--green)',
                  borderRadius: '0 0 10px 10px',
                  padding: '12px 16px',
                  animation: 'slideDown .2s ease',
                }}
              >
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '12px', color: 'var(--muted)', fontSize: '12px' }}>
                    <span className="spinner" /> Loading…
                  </div>
                ) : states.length === 0 ? (
                  <div style={{ fontSize: '12px', color: 'var(--muted)' }}>No state data found</div>
                ) : (
                  <>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--green-dark)', marginBottom: '10px' }}>
                      🗺️ {states.length} states selling this product
                    </div>
                    <div style={{ display: 'grid', gap: '7px' }}>
                      {states.map((s, i) => (
                        <div key={s.state} style={{
                          display: 'grid',
                          gridTemplateColumns: '160px 1fr 95px 55px 70px',
                          alignItems: 'center',
                          gap: '10px',
                          fontSize: '11px',
                        }}>
                          {/* Rank + name */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{
                              width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
                              background: i < 3 ? BAR_COLORS[i] : 'var(--border)',
                              color: i < 3 ? '#fff' : 'var(--muted)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '9px', fontWeight: 700,
                            }}>
                              {i + 1}
                            </span>
                            <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {s.state}
                            </span>
                          </div>

                          {/* Mini bar */}
                          <div style={{ height: '6px', background: 'var(--border)', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{
                              height: '100%', borderRadius: '3px',
                              background: i === 0 ? 'var(--green)' : i === 1 ? 'var(--blue)' : i === 2 ? 'var(--amber)' : 'var(--light)',
                              width: `${(s.revenue / maxRev) * 100}%`,
                              transition: 'width .5s ease',
                            }} />
                          </div>

                          {/* Revenue */}
                          <div style={{ textAlign: 'right', fontWeight: 700, color: 'var(--green)', whiteSpace: 'nowrap' }}>
                            {fmt(s.revenue)}
                          </div>

                          {/* Qty */}
                          <div style={{ textAlign: 'center', color: 'var(--muted)' }}>
                            {s.qty}
                          </div>

                          {/* Bills */}
                          <div style={{ textAlign: 'center' }}>
                            <span style={{
                              background: 'var(--green-light)', color: 'var(--green-dark)',
                              padding: '1px 6px', borderRadius: '999px', fontSize: '10px',
                            }}>
                              {s.total_bills} bills
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          }

          return null;
        })}
      </div>

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
